import {
  type Auth0PluginOptions,
  type Auth0VueClientOptions,
  type Auth0VueClient
} from '@auth0/auth0-vue'
import { ref, type Plugin } from 'vue'
import {
  type RouteLocationNormalizedGeneric,
  type RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { createAuth0 } from '../../composables/useAuth0'
import { AppError, ErrorCategory, ErrorCode } from '../../../error-handling'

/* const sessionStorageCache = {
  get: function (key: string) {
    return JSON.parse(sessionStorage.getItem(key) as string)
  },

  set: function (key: string, value: unknown) {
    sessionStorage.setItem(key, JSON.stringify(value))
  },

  remove: function (key: string) {
    sessionStorage.removeItem(key)
  },

  // Optional
  allKeys: function () {
    return Object.keys(sessionStorage)
  }
} */

let auth0: Auth0VueClient | null = null

interface Auth0Error {
  error: string
  error_description: string
}

export async function getAuthorizationToken(): Promise<string> {
  if (!auth0) {
    throw new Error(
      'Auth0 is not initialized. Please call initializeAuth0 first.'
    )
  }
  try {
    const token = await auth0.getAccessTokenSilently() // Retrieve the token
    return token
  } catch (error) {
    const auth0Error = error as Auth0Error
    if (auth0Error.error === 'invalid_grant') {
      auth0.logout({
        openUrl(url) {}
      })
      // Invalid refresh token etc.
      throw new AppError({
        category: ErrorCategory.AUTH,
        code: ErrorCode.TOKEN_EXPIRED,
        message: 'Authorization failed. Please login again.',
        details: {
          error: auth0Error.error,
          error_description: auth0Error.error_description
        }
      })
    }
    console.error('Invalid token', auth0Error)
    throw new AppError({
      category: ErrorCategory.AUTH,
      code: ErrorCode.TOKEN_INVALID,
      message: 'Authorization failed. Please login again.',
      details: {
        error: auth0Error.error,
        error_description: auth0Error.error_description
      }
    })
  }
}

export const getAuth0Client = () => {
  if (!auth0) {
    throw new Error(
      'Auth0 is not initialized. Please call initializeAuth0 first.'
    )
  }
  return auth0
}

let isAuthenticatedAbsoluteTimeoutFn:
  | ReturnType<typeof setTimeout>
  | undefined = undefined
const DEFAULT_AUTH_TIMEOUT = 5000 // 5 seconds default timeout

export async function isAuthenticated(
  timeoutMs: number = DEFAULT_AUTH_TIMEOUT
) {
  return new Promise<boolean>((resolve, reject) => {
    clearTimeout(isAuthenticatedAbsoluteTimeoutFn)

    // This function will resolve after the specified timeout if the user is not authenticated.
    isAuthenticatedAbsoluteTimeoutFn = setTimeout(() => {
      console.warn(`Authentication check timed out after ${timeoutMs}ms`)
      reject(new Error(`Authentication check timed out after ${timeoutMs}ms`))
    }, timeoutMs)

    try {
      const auth0 = getAuth0Client()
      if (auth0.isLoading.value) {
        // Wait for the plugin to load.
        setTimeout(() => {
          resolve(isAuthenticated(timeoutMs))
        }, 200)
      } else {
        clearTimeout(isAuthenticatedAbsoluteTimeoutFn)
        resolve(auth0.isAuthenticated.value)
      }
    } catch (error) {
      clearTimeout(isAuthenticatedAbsoluteTimeoutFn)
      reject(error)
    }
  })
}

export const history = ref<string[]>([])

function getTargetUrl() {
  let result = '/'
  if (Array.isArray(history.value) && history.value.length > 0) {
    result = history.value[0]
  }
  if (
    result === '/sign-in' ||
    result === '/oauth2-logout' ||
    result === '/oauth2-callback'
  ) {
    return '/'
  }
  return result
}

export async function authRouteGuard(
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric
) {
  // const auth0 = getAuth0Client()
  const authenticated = await isAuthenticated()
  if (!authenticated && history.value.length < 1) {
    history.value.push(to.fullPath)
  }
  if (to.meta.allowPublicEntrance === true && to.name !== from.name) {
    return true
  }
  if (!authenticated) {
    getAuth0Client().loginWithRedirect({
      appState: {
        target: getTargetUrl()
      }
    })
    return false
  }
  return authenticated
}

/** Initialize auth0 client with session storage caching by default. Use cache to override. */
export const initializeAuth0 = (
  clientOptions: Auth0VueClientOptions,
  pluginOptions?: Auth0PluginOptions
) => {
  const auth0Plugin = createAuth0(
    { ...clientOptions, cacheLocation: 'localstorage' },
    pluginOptions
  )
  auth0 = auth0Plugin
  return auth0Plugin as unknown as Plugin
}
